import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBoardList } from '../api/profileService';
import { ROUTES } from '@app/router/routes';
import { useLanguage } from '@shared/contexts/LanguageContext';

export default function BoardDetailPopup({ isOpen, onClose, boardId }) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [boardData, setBoardData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen || !boardId) return;
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getBoardList(boardId);
        if (isMounted && res?.items && res.items.length > 0) {
          setBoardData(res.items[0]);
        }
      } catch (err) {
        console.error('Failed to fetch board details:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      setBoardData(null);
    };
  }, [isOpen, boardId]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  let totalPoints = 0;
  if (boardData?.topics) {
    boardData.topics.forEach(topic => {
      topic.comments?.forEach(comment => {
        comment.point_details?.forEach(detail => {
          totalPoints += (detail.point || 0);
        });
      });
    });
  }

  const dateObj = boardData?.board_start_at ? new Date(boardData.board_start_at) : null;
  const dateStr = dateObj ? `${String(dateObj.getMonth() + 1).padStart(2, '0')}/${String(dateObj.getDate()).padStart(2, '0')}` : '';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
      onClick={handleBackdropClick}
    >
      <div
        className="w-96 p-6 relative bg-white rounded-[20px] shadow-[0px_11px_23px_0px_rgba(0,0,0,0.10)] shadow-[0px_42px_42px_0px_rgba(0,0,0,0.09)] shadow-[0px_95px_57px_0px_rgba(0,0,0,0.05)] shadow-[0px_168px_67px_0px_rgba(0,0,0,0.01)] shadow-[0px_263px_74px_0px_rgba(0,0,0,0.00)] inline-flex flex-col justify-start items-center gap-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="self-stretch inline-flex justify-start items-center gap-8">
          <div className="justify-start text-[#bc313d] text-xl font-bold font-['Noto_Serif_TC'] leading-7 pr-10 break-words">
            {boardData ? `${dateStr} ${boardData.board_title}` : t('board-detail.loading')}
          </div>
        </div>

        {!loading && boardData && (
          <>
            <button
              onClick={onClose}
              aria-label={t('board-detail.closeAria')}
              className="w-6 h-6 absolute right-6 top-6 overflow-hidden cursor-pointer flex-shrink-0 hover:opacity-60 transition-opacity"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="9.25" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" />
                <path d="M7 7L13 13M13 7L7 13" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <div className="self-stretch flex flex-col gap-4">
              {boardData.topics?.map((topic, tIdx) => {
                if (!topic.point_details || topic.point_details.length === 0) return null;
                return (
                  <div key={topic.topic_id || tIdx} className="self-stretch flex flex-col justify-start items-start gap-2">
                    <button
                      className="self-stretch flex justify-between items-center cursor-pointer hover:opacity-70 active:scale-95 transition-all"
                      onClick={() => { onClose(); navigate(ROUTES.LIVE_COMMENT_TOPIC(boardId, topic.topic_id)); }}
                    >
                      <span className="text-stone-500 text-base font-medium font-['Noto_Sans_TC'] leading-7">{topic.topic_title}</span>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 3L11 8L6 13" stroke="#6E625B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    {topic.point_details.map((detail, dIdx) => (
                      <div
                        key={`${topic.topic_id || tIdx}-${dIdx}`}
                        className="self-stretch p-2 bg-[#c59b5a]/20 rounded-sm flex justify-between items-center overflow-hidden"
                      >
                        <div className="justify-start text-[#6e625b] text-sm font-['Noto_Sans_TC'] leading-6 flex-1 truncate pr-2">
                          {detail.description}
                        </div>
                        <div className="text-center justify-center text-[#bc313d] text-sm font-bold font-['Noto_Sans_TC'] leading-6 shrink-0">
                          +{detail.point}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
